import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Container, Rule, Eyebrow, Button, Icon } from "@/brand/ui";
import { useLang } from "@/context/LangContext";
import { AREA_OPTIONS, normQual } from "@/i18n/content";

const LOGO_HEADER = "/brand/logo-horizontal-header-clean.png";
const LOGO_SQUARE = "/brand/logo-square.png";

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 60, behavior: "smooth" });
}

function areaShadow(dark, hover) {
  if (dark) return hover ? "0 18px 40px -20px rgba(15,30,53,0.45)" : "none";
  if (hover) return "inset 0 0 0 1px var(--line), 0 18px 40px -24px rgba(15,30,53,0.25)";
  return "inset 0 0 0 1px var(--line)";
}

export default function Portal() {
  const [chatOpen, setChatOpen] = useState(false);
  const [result, setResult] = useState(null);
  const { t } = useLang();
  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      <Header onChat={() => setChatOpen(true)} />
      <Hero />
      <Audience />
      <Areas />
      <Manifesto />
      <Methodology />
      <Diagnostico result={result} setResult={setResult} />
      <WhyChoose />
      <Faq />
      <ClosingStrip />
      <Footer />
      <ConciergeChat open={chatOpen} onClose={() => setChatOpen(false)} />
      {!chatOpen && (
        <button onClick={() => setChatOpen(true)} data-testid="concierge-fab" aria-label={t.concierge.title}
          style={{ position: "fixed", bottom: 28, right: 28, zIndex: 60, width: 56, height: 56, borderRadius: 2, background: "var(--navy)", color: "var(--off-white)", border: "1px solid rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "var(--shadow-3)" }}>
          <Icon name="sparkle" size={22} />
        </button>
      )}
    </div>
  );
}

function LangSwitch() {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-switch" style={{ display: "flex", alignItems: "center", gap: 4, border: "1px solid var(--line)", borderRadius: 2, padding: 2 }}>
      {["pt", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
          style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.1em", fontWeight: 600, padding: "5px 9px", borderRadius: 1, cursor: "pointer", border: 0,
            background: lang === l ? "var(--navy)" : "transparent", color: lang === l ? "var(--off-white)" : "var(--silver-dark)" }}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Header ---------------- */
function Header() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header style={{ position: "sticky", top: 0, zIndex: 50, background: scrolled ? "rgba(255,255,255,0.92)" : "#ffffff", backdropFilter: scrolled ? "blur(12px)" : "none", borderBottom: scrolled ? "1px solid var(--line)" : "1px solid var(--line-soft)", transition: "all 220ms cubic-bezier(.2,.6,.2,1)" }}>
      <Container>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 0", gap: 16 }}>
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <img src={LOGO_HEADER} alt="Duarte Advocacia Internacional" style={{ height: 68, display: "block" }} />
          </a>
          <nav className="dai-nav" style={{ display: "flex", gap: 34, alignItems: "center" }}>
            {t.nav.map((n) => <NavLink key={n.id} label={n.label} onClick={() => scrollTo(n.id)} />)}
          </nav>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <LangSwitch />
            <Link to="/entrar" data-testid="staff-login-link" style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--silver-dark)", textDecoration: "none", fontWeight: 500 }}>{t.header.equipa}</Link>
            <Button testId="header-diagnostico" onClick={() => scrollTo("diagnostico")}>{t.header.diagnostico}</Button>
          </div>
        </div>
      </Container>
    </header>
  );
}

function NavLink({ label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ position: "relative", background: "transparent", border: 0, cursor: "pointer", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: hover ? "var(--navy)" : "var(--silver-dark)", fontWeight: 500, padding: "8px 0", fontFamily: "var(--font-body)", transition: "color 140ms" }}>
      {label}
      <span style={{ position: "absolute", left: 0, right: 0, bottom: 2, height: 1, background: "var(--navy)", transform: hover ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left center", transition: "transform 180ms cubic-bezier(.2,.6,.2,1)" }} />
    </button>
  );
}

/* ---------------- Hero ---------------- */
function Hero() {
  const { t } = useLang();
  const h = t.hero;
  return (
    <section id="top" style={{ background: "var(--navy)", color: "var(--off-white)", paddingTop: 80, paddingBottom: 130, position: "relative", overflow: "hidden", marginTop: -1 }}>
      <Container>
        <div className="dai-hero-grid" style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 80, alignItems: "end", minHeight: 540 }}>
          <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 30, paddingTop: 110 }}>
            <Eyebrow inverse>{h.eyebrow}</Eyebrow>
            <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(44px, 5.4vw, 76px)", lineHeight: 1.05, letterSpacing: "0.005em", margin: 0, color: "var(--off-white)", maxWidth: 900 }}>
              {h.titleA}<span style={{ color: "var(--silver-light)" }}>{h.titleHi}</span>{h.titleB}
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.7, color: "rgba(255,255,255,0.72)", maxWidth: 640, margin: 0, fontWeight: 300 }}>{h.subtitle}</p>
            <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
              {h.tags.map((p) => <span key={p} style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 500, padding: "7px 12px", color: "var(--silver-light)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 2 }}>{p}</span>)}
            </div>
            <div style={{ display: "flex", gap: 24, alignItems: "center", marginTop: 16, flexWrap: "wrap" }}>
              <Button variant="inversePrimary" icon testId="cta-start-triage" onClick={() => scrollTo("diagnostico")}>{h.ctaPrimary}</Button>
              <Button variant="ghost" inverse onClick={() => scrollTo("areas")}>{h.ctaSecondary}</Button>
            </div>
          </div>
          <div className="dai-hero-aside" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 24, paddingBottom: 12 }}>
            <div style={{ width: 210, height: 210, padding: 18, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)" }}>
              <img src={LOGO_SQUARE} alt="D.A.I." style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
            </div>
            <div style={{ textAlign: "right", color: "rgba(255,255,255,0.6)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase" }}>
              <Rule width={32} color="var(--gold)" /> &nbsp; {h.since}
            </div>
          </div>
        </div>
      </Container>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.1)" }} />
    </section>
  );
}

/* ---------------- Audience ---------------- */
function Audience() {
  const { t } = useLang();
  const a = t.audience;
  return (
    <section id="para-quem" style={{ background: "var(--off-white)", borderBottom: "1px solid var(--line)", padding: "100px 0" }}>
      <Container>
        <div className="dai-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 80, marginBottom: 56, alignItems: "end" }}>
          <div>
            <Eyebrow>{a.eyebrow}</Eyebrow>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(32px, 3.6vw, 48px)", lineHeight: 1.1, color: "var(--navy)", letterSpacing: "0.01em", margin: "20px 0 0" }}>{a.title}</h2>
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.7, color: "var(--silver-dark)", margin: 0, maxWidth: 540 }}>{a.intro}</p>
        </div>
        <div className="dai-grid-5" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
          {a.items.map((it) => (
            <div key={it.n} style={{ borderTop: "1px solid var(--silver-mid)", paddingTop: 18, display: "flex", flexDirection: "column", gap: 12, minHeight: 210 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--gold-deep)", letterSpacing: "0.22em", fontWeight: 600 }}>— {it.n}</div>
              <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 20, lineHeight: 1.2, color: "var(--navy)", margin: 0 }}>{it.t}</h3>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--silver-dark)", margin: 0 }}>{it.d}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------------- Areas ---------------- */
function Areas() {
  const { t } = useLang();
  const a = t.areas;
  return (
    <section id="areas" style={{ padding: "120px 0", background: "var(--bg-elevated)" }}>
      <Container>
        <div className="dai-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 80, marginBottom: 72 }}>
          <div>
            <Eyebrow>{a.eyebrow}</Eyebrow>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(36px, 4vw, 56px)", lineHeight: 1.08, color: "var(--navy)", letterSpacing: "0.01em", margin: "20px 0 0" }}>
              {a.titleLines.map((l, i) => <span key={i}>{l}{i < a.titleLines.length - 1 && <br />}</span>)}
            </h2>
          </div>
          <div style={{ alignSelf: "end" }}>
            <p style={{ fontSize: 16, lineHeight: 1.7, color: "var(--silver-dark)", maxWidth: 560, margin: 0 }}>{a.intro}</p>
          </div>
        </div>
        <div className="dai-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {a.cards.map((c) => <AreaCard key={c.n} a={c} />)}
        </div>
      </Container>
    </section>
  );
}

function AreaCard({ a }) {
  const dark = a.dark;
  const [hover, setHover] = useState(false);
  return (
    <button onClick={() => scrollTo("diagnostico")} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} data-testid={`area-card-${a.n}`}
      style={{ textAlign: "left", cursor: "pointer", background: dark ? "var(--navy)" : "var(--bg-elevated)", color: dark ? "var(--off-white)" : "var(--navy)", borderRadius: 4, padding: "36px 32px 32px", boxShadow: areaShadow(dark, hover), display: "flex", flexDirection: "column", gap: 16, minHeight: 500, border: 0, transform: hover ? "translateY(-3px)" : "none", transition: "transform 220ms cubic-bezier(.2,.6,.2,1), box-shadow 220ms", fontFamily: "var(--font-body)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 44, fontWeight: 400, lineHeight: 1, color: dark ? "var(--gold)" : "var(--gold-deep)" }}>{a.n}</div>
        <Icon name="arrow" size={18} />
      </div>
      <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 24, lineHeight: 1.2, letterSpacing: "0.005em", margin: 0, color: dark ? "var(--off-white)" : "var(--navy)" }}>{a.title}</h3>
      <p style={{ fontSize: 14, lineHeight: 1.65, color: dark ? "rgba(255,255,255,0.72)" : "var(--silver-dark)", margin: 0 }}>{a.lead}</p>
      <ul style={{ listStyle: "none", padding: 0, margin: "auto 0 0", display: "flex", flexDirection: "column", gap: 8 }}>
        {a.items.map((it) => (
          <li key={it} style={{ fontSize: 13, lineHeight: 1.5, color: dark ? "rgba(255,255,255,0.85)" : "var(--navy)", display: "flex", alignItems: "baseline", gap: 12, paddingTop: 8, borderTop: dark ? "1px solid rgba(255,255,255,0.12)" : "1px solid var(--line-soft)" }}>
            <span style={{ width: 4, height: 4, flexShrink: 0, marginTop: 7, background: dark ? "var(--gold)" : "var(--gold-deep)" }} />{it}
          </li>
        ))}
      </ul>
    </button>
  );
}

/* ---------------- Manifesto ---------------- */
function Manifesto() {
  const { t } = useLang();
  const m = t.manifesto;
  return (
    <section style={{ background: "var(--navy)", color: "var(--off-white)", padding: "120px 0" }}>
      <Container>
        <div className="dai-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 80, marginBottom: 64, alignItems: "end" }}>
          <div>
            <Eyebrow inverse gold>{m.eyebrow}</Eyebrow>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(36px, 4vw, 56px)", lineHeight: 1.08, color: "var(--off-white)", letterSpacing: "0.005em", margin: "20px 0 0" }}>
              {m.titleLines[0]}<br /><span style={{ color: "var(--silver-light)" }}>{m.titleLines[1]}</span><br />{m.titleLines[2]}
            </h2>
          </div>
          <p style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontWeight: 400, fontSize: 22, lineHeight: 1.55, color: "rgba(255,255,255,0.85)", margin: 0, maxWidth: 640, borderLeft: "1px solid rgba(255,255,255,0.3)", paddingLeft: 28 }}>{m.quote}</p>
        </div>
        <div className="dai-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, borderTop: "1px solid rgba(255,255,255,0.18)" }}>
          {m.principles.map((p) => (
            <div key={p.n} style={{ padding: "32px 28px 0 0", borderRight: "1px solid rgba(255,255,255,0.12)", display: "flex", flexDirection: "column", gap: 14, minHeight: 190 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--gold)", letterSpacing: "0.22em", fontWeight: 600 }}>— {p.n}</div>
              <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 22, color: "var(--off-white)", margin: 0, lineHeight: 1.2 }}>{p.t}</h3>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: "rgba(255,255,255,0.7)", margin: 0 }}>{p.d}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------------- Methodology ---------------- */
function Methodology() {
  const { t } = useLang();
  const m = t.methodology;
  return (
    <section id="metodo" style={{ background: "var(--paper)", padding: "120px 0", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <Container>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 22, marginBottom: 72, textAlign: "center" }}>
          <Eyebrow gold>{m.eyebrow}</Eyebrow>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(36px, 4vw, 56px)", lineHeight: 1.1, color: "var(--navy)", letterSpacing: "0.01em", margin: 0, maxWidth: 720 }}>{m.title}</h2>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: "var(--silver-dark)", maxWidth: 580, margin: 0 }}>{m.intro}</p>
        </div>
        <div className="dai-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, borderLeft: "1px solid var(--line)" }}>
          {m.steps.map((s) => (
            <div key={s.n} style={{ padding: "32px 28px", borderRight: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 18, minHeight: 320 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <div style={{ fontFamily: "var(--font-serif)", fontSize: 64, fontWeight: 400, lineHeight: 0.9, color: "var(--gold-deep)" }}>{s.n}</div>
                <div style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--silver-mid)", fontWeight: 500 }}>{s.duration}</div>
              </div>
              <Rule width={28} color="var(--silver-mid)" />
              <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 22, color: "var(--navy)", margin: 0, letterSpacing: "0.01em" }}>{s.title}</h3>
              <p style={{ fontSize: 13, lineHeight: 1.65, color: "var(--silver-dark)", margin: 0 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------------- Diagnóstico (intake + AI) ---------------- */
const inputStyle = { width: "100%", background: "var(--bg-elevated)", border: "1px solid var(--line)", borderRadius: 2, padding: "12px 14px", fontSize: 14, color: "var(--navy)", fontFamily: "var(--font-body)", outline: "none" };

function Diagnostico({ result, setResult }) {
  const { t, lang } = useLang();
  const d = t.diagnostico;
  const [form, setForm] = useState({ name: "", email: "", phone: "", area: "", description: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) { toast.error(d.toastFill); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/intake", { ...form, channel: "portal", lang });
      setResult(data);
      toast.success(d.toastOk);
      setTimeout(() => document.getElementById("resultado")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <section id="diagnostico" style={{ background: "var(--bg-elevated)", padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
      <Container>
        <div className="dai-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 80, alignItems: "start" }}>
          <div>
            <Eyebrow gold>{d.eyebrow}</Eyebrow>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(32px, 3.6vw, 48px)", lineHeight: 1.1, color: "var(--navy)", letterSpacing: "0.01em", margin: "20px 0 0" }}>{d.title}</h2>
            <p style={{ fontSize: 15, lineHeight: 1.7, color: "var(--silver-dark)", marginTop: 20, maxWidth: 420 }}>{d.intro}</p>
            <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 12 }}>
              {d.features.map(([label, ic]) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 12, color: "var(--navy)", fontSize: 14 }}>
                  <span style={{ color: "var(--gold-deep)" }}><Icon name={ic} size={18} /></span>{label}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={submit} data-testid="intake-form" style={{ background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 4, padding: 36, display: "flex", flexDirection: "column", gap: 20 }}>
            <div className="dai-form-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <Field label={d.fields.name} required><input data-testid="intake-name" style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={d.placeholders.name} /></Field>
              <Field label={d.fields.email} required><input data-testid="intake-email" type="email" style={inputStyle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={d.placeholders.email} /></Field>
            </div>
            <div className="dai-form-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <Field label={d.fields.phone}><input data-testid="intake-phone" style={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={d.placeholders.phone} /></Field>
              <Field label={d.fields.area}>
                <select data-testid="intake-area" style={inputStyle} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}>
                  <option value="">{d.placeholders.area}</option>
                  {AREA_OPTIONS[lang].map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
            </div>
            <Field label={d.fields.description} required>
              <textarea data-testid="intake-description" rows={5} style={{ ...inputStyle, resize: "vertical" }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={d.placeholders.description} />
            </Field>
            <Button type="submit" icon disabled={loading} testId="submit-intake-form" style={{ width: "100%" }}>{loading ? d.submitting : d.submit}</Button>
          </form>
        </div>

        {result && <div id="resultado" className="fade-up" style={{ marginTop: 40 }}><TriageResult result={result} /></div>}
      </Container>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500, marginBottom: 8 }}>
        {label}{required && <span style={{ color: "var(--gold-deep)" }}> *</span>}
      </span>
      {children}
    </label>
  );
}

const qualColor = { quente: "var(--success)", morno: "var(--warning)", frio: "var(--info)" };

function TriageResult({ result }) {
  const { t } = useLang();
  const tr = t.triage;
  const d = result.triage;
  const score = d.elegibilidade_score ?? 0;
  const nq = normQual(d.qualificacao);
  return (
    <div data-testid="triage-result" style={{ background: "var(--bg-elevated)", border: "1px solid var(--line)", borderTop: "3px solid var(--gold-deep)", borderRadius: 4, padding: 40 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, borderBottom: "1px solid var(--line)", paddingBottom: 24 }}>
        <div>
          <Eyebrow gold>{tr.resultEyebrow} · {result.ref}</Eyebrow>
          <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 30, color: "var(--navy)", margin: "14px 0 0" }}>{d.area}</h3>
        </div>
        <span style={{ padding: "6px 14px", borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#fff", background: qualColor[nq] }}>{tr.lead} {tr.qual[nq]}</span>
      </div>
      <div className="dai-triage" style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 48, marginTop: 32 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500 }}>{tr.score}</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginTop: 10 }}>
            <span style={{ fontFamily: "var(--font-serif)", fontSize: 72, fontWeight: 500, lineHeight: 0.9, color: "var(--navy)" }}>{score}</span>
            <span style={{ color: "var(--silver-mid)", marginBottom: 10, fontFamily: "var(--font-mono)" }}>/100</span>
          </div>
          <div style={{ marginTop: 14, height: 4, background: "var(--pearl)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${score}%`, background: "var(--gold-deep)" }} />
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--silver-dark)", marginTop: 14 }}>{d.elegibilidade_justificativa}</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500, marginBottom: 8 }}>{tr.resumo}</div>
            <p style={{ fontSize: 15, lineHeight: 1.7, color: "var(--navy)", margin: 0 }}>{d.resumo}</p>
          </div>
          <div className="dai-form-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500, marginBottom: 10 }}>{tr.checklist}</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {(d.checklist_documentos || []).map((x, i) => <li key={`chk-${i}-${x.slice(0, 12)}`} style={{ display: "flex", gap: 10, fontSize: 13, color: "var(--navy)", lineHeight: 1.5 }}><span style={{ color: "var(--success)", flexShrink: 0, marginTop: 1 }}><Icon name="check" size={15} /></span>{x}</li>)}
              </ul>
            </div>
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500, marginBottom: 10 }}>{tr.riscos}</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {(d.riscos || []).map((x, i) => <li key={`risk-${i}-${x.slice(0, 12)}`} style={{ display: "flex", gap: 10, fontSize: 13, color: "var(--navy)", lineHeight: 1.5 }}><span style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }}><Icon name="alert" size={15} /></span>{x}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid var(--line)" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500, marginBottom: 10 }}>{tr.proposta} · {d.servico_recomendado}</div>
        <p style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 18, lineHeight: 1.6, color: "var(--navy)", margin: 0, maxWidth: 760 }}>{d.proposta}</p>
        <p style={{ fontSize: 13, color: "var(--gold-deep)", marginTop: 16 }}>{tr.contactNote} <span style={{ fontFamily: "var(--font-mono)" }}>{result.ref}</span></p>
      </div>
    </div>
  );
}

/* ---------------- WhyChoose ---------------- */
function WhyChoose() {
  const { t } = useLang();
  const w = t.whychoose;
  return (
    <section id="porque" style={{ background: "var(--navy)", color: "var(--off-white)", padding: "120px 0" }}>
      <Container>
        <div className="dai-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 80, marginBottom: 72, alignItems: "end" }}>
          <div>
            <Eyebrow inverse gold>{w.eyebrow}</Eyebrow>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(36px, 4vw, 56px)", lineHeight: 1.08, color: "var(--off-white)", letterSpacing: "0.01em", margin: "20px 0 0" }}>{w.title}</h2>
          </div>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: "rgba(255,255,255,0.72)", maxWidth: 560, margin: 0 }}>{w.intro}</p>
        </div>
        <div className="dai-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0, borderLeft: "1px solid rgba(255,255,255,0.14)" }}>
          {w.pillars.map((p) => (
            <div key={p.n} style={{ padding: 32, borderRight: "1px solid rgba(255,255,255,0.14)", borderBottom: "1px solid rgba(255,255,255,0.14)", display: "flex", flexDirection: "column", gap: 16, minHeight: 220 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.22em", color: "var(--gold)", fontWeight: 600 }}>— {p.n}</div>
              <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 24, lineHeight: 1.2, color: "var(--off-white)", margin: 0 }}>{p.t}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.65, color: "rgba(255,255,255,0.72)", margin: 0 }}>{p.d}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function Faq() {
  const { t } = useLang();
  const f = t.faq;
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" style={{ background: "var(--bg-elevated)", padding: "120px 0", borderTop: "1px solid var(--line)" }}>
      <Container>
        <div className="dai-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 80, alignItems: "start" }}>
          <div>
            <Eyebrow>{f.eyebrow}</Eyebrow>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(32px, 3.6vw, 48px)", lineHeight: 1.1, color: "var(--navy)", letterSpacing: "0.01em", margin: "20px 0 0" }}>{f.titleLines[0]}<br />{f.titleLines[1]}</h2>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: "var(--silver-dark)", marginTop: 24, maxWidth: 320 }}>{f.note}</p>
          </div>
          <div style={{ borderTop: "1px solid var(--line)" }}>
            {f.items.map((it, i) => {
              const isOpen = open === i;
              return (
                <div key={it.q} style={{ borderBottom: "1px solid var(--line)" }}>
                  <button onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`faq-${i}`}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "26px 0", background: "transparent", border: 0, cursor: "pointer", textAlign: "left", gap: 24 }}>
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 19, color: "var(--navy)", letterSpacing: "0.005em", lineHeight: 1.35 }}>{it.q}</span>
                    <span style={{ color: "var(--silver-dark)", flexShrink: 0, transition: "transform 220ms", transform: isOpen ? "rotate(45deg)" : "none" }}><Icon name="plus" size={16} /></span>
                  </button>
                  {isOpen && <div style={{ paddingBottom: 26, paddingRight: 60 }}><p style={{ fontSize: 14, lineHeight: 1.75, color: "var(--silver-dark)", margin: 0, maxWidth: 720 }}>{it.a}</p></div>}
                </div>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ---------------- ClosingStrip ---------------- */
function ClosingStrip() {
  const { t } = useLang();
  const c = t.closing;
  return (
    <section style={{ background: "var(--paper)", padding: "100px 0", borderTop: "1px solid var(--line)" }}>
      <Container>
        <div className="dai-2col" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 80, alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            <Eyebrow>{c.eyebrow}</Eyebrow>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 200, fontSize: "clamp(32px, 3.6vw, 52px)", lineHeight: 1.08, color: "var(--navy)", letterSpacing: "0.005em", margin: 0 }}>{c.title}</h2>
            <p style={{ fontSize: 16, lineHeight: 1.7, color: "var(--silver-dark)", margin: 0, maxWidth: 560 }}>{c.intro}</p>
            <div style={{ display: "flex", gap: 18, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              <Button icon onClick={() => scrollTo("diagnostico")}>{c.ctaPrimary}</Button>
              <Button variant="ghost" onClick={() => scrollTo("faq")}>{c.ctaSecondary}</Button>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", borderTop: "1px solid var(--line)" }}>
            {c.contact.map(([k, v]) => (
              <div key={k} style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 18, padding: "16px 0", borderBottom: "1px solid var(--line)", alignItems: "baseline" }}>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500 }}>{k}</div>
                <div style={{ fontSize: 14, color: "var(--navy)", lineHeight: 1.5 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ---------------- Footer ---------------- */
function FooterLegalLink({ slug, label }) {
  const [hover, setHover] = useState(false);
  return (
    <Link to={`/${slug}`} data-testid={`footer-legal-${slug}`} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 500, textDecoration: "none", transition: "color 140ms",
        color: hover ? "var(--off-white)" : "rgba(255,255,255,0.6)" }}>
      {label}
    </Link>
  );
}

export function Footer() {
  const { t } = useLang();
  const f = t.footer;
  return (
    <footer style={{ background: "var(--navy)", color: "var(--off-white)", paddingTop: 96, paddingBottom: 32 }}>
      <Container>
        <div className="dai-footer" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 56, paddingBottom: 72, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 16 }}>
            <img src={LOGO_SQUARE} alt="" style={{ width: 96, height: 96, objectFit: "contain", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.2)" }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 400, fontSize: 14, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--silver-light)" }}>{f.brand[0]}</div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 400, fontSize: 14, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--silver-light)" }}>{f.brand[1]}</div>
            </div>
          </div>
          {f.cols.map((col) => (
            <div key={col.h} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--silver-light)", fontWeight: 500, marginBottom: 6 }}>{col.h}</div>
              {col.links.map((l) => <span key={l} style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>{l}</span>)}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 30, padding: "28px 0 0" }} data-testid="footer-legal-links">
          {(f.legal || []).map(([slug, label]) => <FooterLegalLink key={slug} slug={slug} label={label} />)}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 24, gap: 24, flexWrap: "wrap" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "rgba(255,255,255,0.45)", letterSpacing: "0.04em" }}>{f.rights}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "rgba(255,255,255,0.35)", letterSpacing: "0.04em" }}>{f.powered}</div>
        </div>
      </Container>
    </footer>
  );
}

/* ---------------- Concierge Chat ---------------- */
function ConciergeChat({ open, onClose }) {
  const { t, lang } = useLang();
  const c = t.concierge;
  const mkMsg = (role, content) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, role, content });
  const [messages, setMessages] = useState(() => [mkMsg("assistant", c.greeting)]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { setMessages([mkMsg("assistant", c.greeting)]); setSessionId(null); }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);
  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput(""); setMessages((m) => [...m, mkMsg("user", msg)]); setSending(true);
    try {
      const { data } = await api.post("/concierge/chat", { session_id: sessionId, message: msg, lang });
      setSessionId(data.session_id); setMessages((m) => [...m, mkMsg("assistant", data.reply)]);
    } catch { setMessages((m) => [...m, mkMsg("assistant", c.error)]); }
    finally { setSending(false); }
  };
  if (!open) return null;
  return (
    <div data-testid="concierge-panel" className="fade-up" style={{ position: "fixed", bottom: 28, right: 28, zIndex: 70, width: "92vw", maxWidth: 400, height: "68vh", background: "var(--bg-elevated)", border: "1px solid var(--line)", borderRadius: 4, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-4)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", background: "var(--navy)", color: "var(--off-white)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="ai-pulse" style={{ color: "var(--gold)" }}><Icon name="sparkle" size={18} /></span>
          <span style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 500 }}>{c.title}</span>
        </div>
        <button onClick={onClose} data-testid="close-concierge" style={{ background: "transparent", border: 0, color: "rgba(255,255,255,0.7)", cursor: "pointer" }}><Icon name="x" size={18} /></button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 12, background: "var(--paper)" }}>
        {messages.map((m) => (
          <div key={m.id} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "82%", padding: "10px 14px", borderRadius: 4, fontSize: 14, lineHeight: 1.55, background: m.role === "user" ? "var(--navy)" : "var(--bg-elevated)", color: m.role === "user" ? "var(--off-white)" : "var(--navy)", border: m.role === "user" ? "0" : "1px solid var(--line)" }}>{m.content}</div>
          </div>
        ))}
        {sending && <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--gold-deep)" }} className="ai-pulse">{c.writing}</div>}
        <div ref={endRef} />
      </div>
      <div style={{ padding: 12, borderTop: "1px solid var(--line)", display: "flex", gap: 8 }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="concierge-input" placeholder={c.placeholder} style={{ ...inputStyle, flex: 1 }} />
        <button onClick={send} disabled={sending} data-testid="concierge-send" style={{ background: "var(--navy)", color: "var(--off-white)", border: 0, borderRadius: 2, padding: "0 16px", cursor: "pointer" }}><Icon name="send" size={16} /></button>
      </div>
    </div>
  );
}
